import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BreakpointObserver, BreakpointState, LayoutModule } from '@angular/cdk/layout';
import { Observable } from 'rxjs';
import {
  ToolbarComponent,
  SidebarComponent,
  MenuItemGroup,
  MenuItem,
  ToolbarUser,
} from 'wilshire-ui/layout';
import { MENU_GROUPS } from '../../../core/constants/menu-items';
import { AuthService } from '../../../core/services/auth/auth.service';

@Component({
  selector: 'wu-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    LayoutModule,
    ToolbarComponent,
    SidebarComponent,
  ],
  template: `
    <div class="app-container">
      <!-- Skip navigation link (WCAG 2.4.1 — Bypass Blocks) -->
      <!-- Use (click) + $event.preventDefault() to avoid Angular Router treating #main-content as a route -->
      <a href="#main-content" class="skip-link" (click)="skipToMain($event)"
        >Skip to main content</a
      >

      <!-- Toolbar -->
      <app-toolbar [user]="currentUser" [sideNavRef]="snav" (signOut)="onSignOut()"> </app-toolbar>

      <!-- Sidenav Container -->
      <mat-sidenav-container>
        <mat-sidenav
          #snav
          [mode]="(breakpointMatches$ | async)?.matches ? 'side' : 'over'"
          [(opened)]="sidenavOpened"
          role="navigation"
        >
          <app-sidebar
            [sidenavOpened]="sidenavOpened"
            [menuItems]="menuItems"
            [permissions]="[]"
            [roleId]="0"
          >
          </app-sidebar>
        </mat-sidenav>

        <mat-sidenav-content role="main">
          <div id="main-content" class="nav-main">
            <ng-content></ng-content>
          </div>
        </mat-sidenav-content>
      </mat-sidenav-container>
    </div>
  `,
  styles: [
    `
      /* Skip link — visually hidden until focused (WCAG 2.4.1) */
      .skip-link {
        position: absolute;
        top: -100%;
        left: 0;
        background: #000;
        color: #fff;
        padding: 8px 16px;
        font-size: 14px;
        z-index: 9999;
        text-decoration: none;

        &:focus {
          top: 0;
        }
      }

      .app-container {
        display: flex;
        flex-direction: column;
        height: 100vh;
      }

      mat-sidenav-container {
        flex: 1;
      }

      mat-sidenav {
        width: 250px;
      }

      mat-sidenav-content {
        display: flex;
        flex-direction: column;
      }

      .nav-main {
        flex: 1;
        padding: 2rem;
      }
    `,
  ],
})
export class LayoutComponent implements OnInit {
  currentUser: ToolbarUser | null = null;
  sidenavOpened = false;
  menuItems: (MenuItemGroup | MenuItem)[] = [];
  breakpointMatches$: Observable<BreakpointState>;

  constructor(
    private authService: AuthService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
  ) {
    // Observe screen size for responsive sidenav mode
    // Desktop (>= 600px): 'side' mode (pushes content)
    // Mobile (< 600px): 'over' mode (overlay)
    this.breakpointMatches$ = this.breakpointObserver.observe(['(min-width: 600px)']);
  }

  ngOnInit() {
    this.menuItems = MENU_GROUPS;
    this.loadCurrentUser();
  }

  /**
   * Loads the current user profile from Cognito
   */
  private loadCurrentUser() {
    this.authService.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUser = {
          firstName: user.givenName || 'User',
          lastName: user.familyName || '',
          email: user.email,
        };
      },
      error: (err) => {
        console.error('Error loading user profile:', err);
        // Set default user if loading fails
        this.currentUser = {
          firstName: 'User',
          lastName: '',
          email: '',
        };
      },
    });
  }

  /**
   * Moves focus to the main content area without triggering Angular Router navigation.
   * Prevents the default href="#main-content" from being treated as a route change.
   */
  skipToMain(event: Event) {
    event.preventDefault();
    const main = document.getElementById('main-content');
    if (main) {
      main.setAttribute('tabindex', '-1');
      main.focus();
    }
  }

  /**
   * Signs out the current user and redirects to home page
   */
  async onSignOut() {
    try {
      await this.authService.signOutUser();
      // Redirect to home page (which will trigger federated sign-in)
      window.location.href = '/';
    } catch (err) {
      console.error('Error signing out:', err);
    }
  }
}
